import React, { useState, useEffect, useRef } from "react"
import {
  chatContainer,
  chatButton,
  chatButtonOpen,
  chatPanel,
  chatPanelOpen,
  chatHeader,
  chatTitle,
  chatClose,
  chatBody,
  chatBubble,
  chatBubbleClose,
} from "./persistent-chat.module.css"
import { trackEvent } from "../../modules/analytics"

const SCRIPT_SRC = "https://mkbsd-ai.web.app/release/ai-ui.js?b=2506091220"

const PersistentChat = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const bubbleDismissedRef = useRef(false)
  const chatBodyRef = useRef(null)
  const aiElementRef = useRef(null)
  const containerRef = useRef(null)

  // Suppress bubble for 1 week if user dismissed it via ✕
  useEffect(() => {
    const suppressed = localStorage.getItem("chat-bubble-suppressed")
    if (suppressed && Date.now() < parseInt(suppressed, 10)) {
      bubbleDismissedRef.current = true
    }
  }, [])

  // Show bubble after 30% scroll (once, until dismissed)
  useEffect(() => {
    const handleScroll = () => {
      if (bubbleDismissedRef.current) return
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight
      if (pageHeight <= 0) return
      if (window.scrollY / pageHeight >= 0.3) {
        setShowBubble(true)
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close when clicking outside the chat container
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    // Use capture so we catch clicks before they're consumed by child elements
    document.addEventListener("mousedown", handleClickOutside, true)
    return () => document.removeEventListener("mousedown", handleClickOutside, true)
  }, [isOpen])

  // Mount mkbsd-ai once — never destroy it so the session survives navigation
  useEffect(() => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`)

    const mount = () => {
      if (!chatBodyRef.current || aiElementRef.current) return
      const el = document.createElement("mkbsd-ai")
      // Pick up article context that may have been set before this element mounted
      const articleId = window.__mkbsdAiArticleId
      if (articleId) el.setAttribute("article-id", articleId)
      chatBodyRef.current.appendChild(el)
      aiElementRef.current = el
    }

    if (existing && customElements.get("mkbsd-ai")) {
      mount()
    } else {
      const script = existing ?? document.createElement("script")
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = mount
      if (!existing) document.body.appendChild(script)
    }

    // Allow article pages to update the chat context as the user navigates
    const handleContext = e => {
      if (e.detail?.articleId) {
        window.__mkbsdAiArticleId = e.detail.articleId
        if (aiElementRef.current) {
          aiElementRef.current.setAttribute("article-id", e.detail.articleId)
        }
      }
    }
    document.addEventListener("mkbsd-ai-context", handleContext)

    return () => {
      document.removeEventListener("mkbsd-ai-context", handleContext)
      // Intentionally do NOT remove the script or the element —
      // keeping the DOM node alive preserves the chat session.
    }
  }, [])

  const dismissBubble = () => {
    bubbleDismissedRef.current = true
    setShowBubble(false)
  }

  const dismissBubblePermanently = e => {
    e.stopPropagation()
    const oneWeek = Date.now() + 7 * 24 * 60 * 60 * 1000
    localStorage.setItem("chat-bubble-suppressed", String(oneWeek))
    dismissBubble()
  }

  const openChatFromBubble = () => {
    dismissBubble()
    setIsOpen(true)
    trackEvent("floating_chat_open")
  }

  const handleToggle = () => {
    const opening = !isOpen
    setIsOpen(opening)
    if (opening) {
      trackEvent("floating_chat_open")
      dismissBubble()
    }
  }

  return (
    <div className={chatContainer} ref={containerRef}>
      {/* Panel is always in the DOM so the mkbsd-ai session is never destroyed */}
      <dialog
        className={`${chatPanel} ${isOpen ? chatPanelOpen : ""}`}
        open
        aria-label="AI Assistent"
        aria-hidden={!isOpen}
      >
        <div className={chatHeader}>
          <span className={chatTitle}>AI Assistent</span>
          <button
            className={chatClose}
            onClick={() => setIsOpen(false)}
            type="button"
            aria-label="Sluit chat"
          >
            ✕
          </button>
        </div>
        <div className={chatBody} ref={chatBodyRef} />
      </dialog>
      {showBubble && !isOpen && (
        <div
          className={chatBubble}
          onClick={openChatFromBubble}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === "Enter" && openChatFromBubble()}
        >
          Direct antwoord op je ondernemersvragen? Onze AI-assistent denkt graag met je mee, of het nu over een artikel gaat of een brede vraag is.
          <button
            className={chatBubbleClose}
            onClick={dismissBubblePermanently}
            type="button"
            aria-label="Sluit hint"
          >
            ✕
          </button>
        </div>
      )}
      <button
        className={`${chatButton} ${isOpen ? chatButtonOpen : ""}`}
        onClick={handleToggle}
        type="button"
        aria-label={isOpen ? "Sluit AI assistent" : "Open AI assistent"}
        title={isOpen ? "Sluit AI assistent" : "Open AI assistent"}
      >
        <svg viewBox="0 0 24 24" width="32" height="32">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.1266 22.1995C16.7081 22.5979 17.4463 23.0228 18.3121 23.3511C19.9903 23.9874 21.244 24.0245 21.8236 23.9917C23.1167 23.9184 23.2907 23.0987 22.5972 22.0816C21.8054 20.9202 21.0425 19.6077 21.1179 18.1551C22.306 16.3983 23 14.2788 23 12C23 5.92487 18.0751 1 12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23C13.4578 23 14.8513 22.7159 16.1266 22.1995ZM12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C13.3697 21 14.6654 20.6947 15.825 20.1494C16.1635 19.9902 16.5626 20.0332 16.8594 20.261C17.3824 20.6624 18.1239 21.1407 19.0212 21.481C19.4111 21.6288 19.7674 21.7356 20.0856 21.8123C19.7532 21.2051 19.4167 20.4818 19.2616 19.8011C19.1018 19.0998 18.8622 17.8782 19.328 17.2262C20.3808 15.7531 21 13.9503 21 12C21 7.02944 16.9706 3 12 3Z"
            fill="#fff"
            opacity="0.8"
          />
          <text
            x="12"
            y="15"
            opacity="0.8"
            textAnchor="middle"
            fontSize="8"
            fontWeight="700"
            fill="currentColor"
            fontFamily="Arial, sans-serif"
          >
            AI
          </text>
        </svg>
      </button>
    </div>
  )
}

export default PersistentChat
